import React, { useState } from "react";
import { UPDP_BUYBOX_MODES, flagsAndExperimentNames } from "rtr-constants";
import { useSelector, useDispatch } from "react-redux";
import Chevron from "images/design-system-svgs/chevron.svg";
import Calendar from "images/design-system-svgs/calendar.svg";
import { setPDPBuyBoxMode } from "slices/pdp-slice";
import { isSubscriptionMember, hasExtraSpot } from "helpers/membership-helpers";
import { useAvailabilityFilters } from "hooks/use-availability-filters";
import { useWorkingFilter } from "hooks/use-working-filter";
import { normalizePrice } from "helpers/PricingHelper";
import Routes from "routes";
import { useOnLoadFlagOrExperiment } from "hooks/use-flags-and-experiments";
import flagsAndExperimentsNames from "constants/flags-and-experiments-names";
import { shouldExcludePromo } from "helpers/product-drawer-helper";

const ModeSwitcher = () => {
  const buyBoxMode = useSelector(state => state.pdp?.pdpBuyBoxMode);
  const dispatch = useDispatch();
  const userData = useSelector(state => state.userData);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);
  const buyPricing = useSelector(state => state.pdp?.buy?.pricing?.price);
  const reservePricing = useSelector(state => state.pdp?.reserve?.pricing?.price);
  const { setOrToggleMembershipAvailability } = useAvailabilityFilters(() => {});
  const [workingFiltersZipCode, setWorkingFiltersZipCode] = useWorkingFilter("zip_code", () => {});
  const [workingFiltersDate] = useWorkingFilter("date", () => {});
  const [workingFiltersDuration] = useWorkingFilter("duration", () => {});
  const { setReserveAvailability } = useAvailabilityFilters(() => {});
  const [reserveFilters, setReserveFilters] = useState({ date: null, duration: null });
  const proratedPrice = useSelector(state => state.proratedPrice?.addonPrice);
  const promoPillSub = useSelector(state => state.smartComponentState?.PDPContent?.data?.membership?.pill);
  const promoPillReserve = useSelector(state => state.smartComponentState?.PDPContent?.data?.reserve?.pill);
  const promoPillBuy = useSelector(state => state.smartComponentState?.PDPContent?.data?.buy?.pill);
  const staticProduct = useSelector(state => state.staticProduct);
  const promoExclusionFlag = useSelector(
    state => state.flagsAndExperiments?.[flagsAndExperimentNames.CATALOG_SERVICES_SHOW_PROMO_EXCLUSION]
  );

  const excludePromo = shouldExcludePromo({
    product: staticProduct,
    promoExclusionFlag,
  });

  const [addonPriceFF] = useOnLoadFlagOrExperiment(
    flagsAndExperimentsNames.SWAT_1162_PRORATED_ADDON_PRICE_FEATURE_TOGGLE
  );

  const proratedAddonPrice = addonPriceFF && proratedPrice ? proratedPrice : null;

  // check the url params for buynow=true, if so, clear buynow from params
  const clearBuyNowFromUrl = () => {
    const url = new URL(window.location);
    if (url.searchParams.get("buynow") === "true") {
      url.searchParams.delete("buynow");
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  const pill = (color, content) =>
    content ? (
      <div data-test-id="promo-pill" className={`rounded-full px-2 py-1 text-sm ${color}`}>
        {content}
      </div>
    ) : null;

  const membership = () => {
    const extraSpotPrice = hasExtraSpot(membershipState, ceBagCount, proratedAddonPrice);
    const baseExtraSpotPrice = hasExtraSpot(membershipState, ceBagCount, null);

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <span>Rent with subscription</span>
          {pill("bg-darkPink", promoPillSub)}
        </div>

        {!isSubscriptionMember(userData) ? (
          <p className="text-darkGray text-sm">
            Rent 5 items at a time with our flexible{" "}
            <a className="text-onyx underline" href={Routes.LP.plans}>
              monthly plans
            </a>
          </p>
        ) : extraSpotPrice ? (
          <p>
            {extraSpotPrice && extraSpotPrice !== baseExtraSpotPrice ? (
              <span className="text-sm line-through mr-1">{baseExtraSpotPrice}</span>
            ) : null}
            <span className="font-semibold">{extraSpotPrice}</span>
            <span className="text-darkGray mx-1">|</span>
            <span className="text-darkGray text-sm">Add an extra spot</span>
          </p>
        ) : (
          <p className="flex items-center">
            <span className="font-semibold">$0</span>
            <span className="text-darkGray mx-1">|</span>
            <span className="text-darkGray text-sm">Included with your subscription</span>
          </p>
        )}
      </div>
    );
  };

  const reserve = () => {
    if (!reservePricing)
      return (
        <div className="animate-pulse flex flex-col h-11 w-full gap-2">
          <div className="h-full bg-offWhite rounded"></div>
          <div className="h-full bg-offWhite rounded"></div>
        </div>
      );

    const price = reservePricing.find(price => price.id === "rental")?.value;
    const memberPrice = reservePricing.find(price => price.id === "memberrental")?.value;
    const displayPrice = memberPrice || price;
    const memberDiscount = reservePricing.find(price => price.id === "memberrental")?.discount;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <Calendar className="w-5 mr-1.5" />
          <p className="mr-4">One-off rental</p>
          {pill("bg-lilac", promoPillReserve)}
        </div>
        <p className="flex items-baseline">
          <span className="text-sm mr-1">Starting at</span>
          {memberPrice ? <span className="text-sm mr-1 line-through">{normalizePrice(price)}</span> : null}
          <span className="font-semibold">{normalizePrice(displayPrice)}</span>
          <span className="text-darkGray mx-1">|</span>
          {memberPrice ? (
            <span className="text-tealGreen text-sm">Members get {memberDiscount * 100}% off</span>
          ) : (
            <span className="text-darkGray text-sm">No subscription required</span>
          )}
        </p>
      </div>
    );
  };

  const buy = () => {
    if (!buyPricing)
      return (
        <div className="animate-pulse flex flex-col h-11 w-full gap-2">
          <div className="h-full bg-offWhite rounded"></div>
          <div className="h-full bg-offWhite rounded"></div>
        </div>
      );

    const msrp = buyPricing.find(price => price.id === "msrp")?.value;
    const price = buyPricing.find(price => price.id === "buy")?.value;
    const discount = buyPricing.find(price => price.id === "buy")?.discount;
    const discountPercent = typeof discount === "number" ? Math.round(discount * 100) : null;

    return (
      <div data-test-id="buy-mode-switcher" className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <span>Buy it</span>
          {excludePromo ? null : pill("bg-lightTealGreen", promoPillBuy)}
        </div>

        <div className="flex items-baseline">
          {discount ? <span className="text-sm line-through mr-1">${msrp}</span> : null}
          <span className="font-semibold">{normalizePrice(price)}</span>
          {discount ? (
            <>
              <span className="text-darkGray mx-1">|</span>
              <span className="text-mutedRed text-sm">{discountPercent}% off retail</span>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  let options = [];

  if (buyBoxMode === UPDP_BUYBOX_MODES.MEMBERSHIP) {
    options = [
      { mode: UPDP_BUYBOX_MODES.RESERVE, content: reserve() },
      { mode: UPDP_BUYBOX_MODES.BUY, content: buy() },
    ];
  } else if (buyBoxMode === UPDP_BUYBOX_MODES.RESERVE) {
    options = [
      { mode: UPDP_BUYBOX_MODES.MEMBERSHIP, content: membership() },
      { mode: UPDP_BUYBOX_MODES.BUY, content: buy() },
    ];
  } else if (buyBoxMode === UPDP_BUYBOX_MODES.BUY) {
    options = [
      { mode: UPDP_BUYBOX_MODES.MEMBERSHIP, content: membership() },
      { mode: UPDP_BUYBOX_MODES.RESERVE, content: reserve() },
    ];
  }

  const onClick = newMode => {
    if (buyBoxMode === UPDP_BUYBOX_MODES.BUY && newMode !== UPDP_BUYBOX_MODES.BUY) {
      clearBuyNowFromUrl();
    }
    // Switching buyBoxMode away from "reserve" calls a hook shared with grid component that clears date/duration from workingFilter cookies (this allows the correct mode to persist through the journey).
    // To prevent the user from losing their chosen dates if they’re just exploring other modes but intend to switch back to reserve, we:
    //   - Save the current workingFilters (date + duration) into component state when leaving "reserve".
    //   - Restore them from component state (if available) when returning to "reserve" and workingFilters are empty.
    if (buyBoxMode === UPDP_BUYBOX_MODES.RESERVE) {
      setReserveFilters({ date: workingFiltersDate, duration: workingFiltersDuration });
    }

    if (newMode === UPDP_BUYBOX_MODES.RESERVE && reserveFilters.date) {
      setReserveAvailability(reserveFilters.date, reserveFilters.duration);
    }

    // Other modes allow you to enter random zip codes, however membership buybox uses a saved sub address.
    // When the user switches to membership mode, we need to ensure the workingFiltersZipCode
    // (which informs the depot id) is set back to the default membership address.
    if (newMode === UPDP_BUYBOX_MODES.MEMBERSHIP) {
      setOrToggleMembershipAvailability();

      if (
        membershipState?.address?.postalCode &&
        newMode === UPDP_BUYBOX_MODES.MEMBERSHIP &&
        workingFiltersZipCode !== membershipState?.address?.postalCode
      ) {
        setWorkingFiltersZipCode(membershipState?.address?.postalCode);
      }
    }

    dispatch(setPDPBuyBoxMode(newMode));

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (options.length === 0) return null;

  return (
    <div data-test-id="pdp-mode-switcher" className="border-y-4 border-offWhite bg-softWhite -mx-4 p-4">
      <p className="text-lg mb-4">Other Ways to Get It</p>
      <div className="space-y-2">
        {options.map((option, idx) => (
          <button
            data-heap-id={`pdp-mode-switch-${option.mode}`}
            className="p-3 border border-lightGray bg-white rounded-lg flex items-center justify-between w-full text-start"
            key={idx}
            onClick={() => onClick(option.mode)}>
            <div className="flex-1">{option.content}</div>
            <Chevron className="ml-2 mr-1" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ModeSwitcher;
